import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Play, Pause, Plus, Check, Music2, SkipBack, SkipForward, Disc3 } from 'lucide-react';
import { SOUNDTRACK } from '../mock/mock';
import { useCart } from '../context/CartContext';

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const MusicPlayer = () => {
  const { addItem } = useCart();
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [added, setAdded] = useState(null);
  const cap = SOUNDTRACK.previewSeconds || 30;
  const tracks = SOUNDTRACK.tracks || [];
  const track = tracks[current];

  const stopAndReset = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setPlaying(false);
    setTime(0);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      if (audio.currentTime >= cap) stopAndReset();
      else setTime(audio.currentTime);
    };
    const onEnded = () => stopAndReset();
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
    };
  }, [cap, stopAndReset]);

  const playIndex = (idx) => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrent(idx);
    setTime(0);
    setTimeout(() => {
      audio.currentTime = 0;
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }, 60);
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const next = () => playIndex((current + 1) % tracks.length);
  const prev = () => playIndex((current - 1 + tracks.length) % tracks.length);

  const addSingle = (t) => {
    addItem({ id: `song-${t.id}`, lookup_key: t.lookup_key, name: `${t.title} (Single)`, price: t.price, img: SOUNDTRACK.cover }, 1);
    setAdded(t.id);
    setTimeout(() => setAdded((c) => (c === t.id ? null : c)), 1400);
  };

  const addFull = () => addItem(SOUNDTRACK.fullProduct, 1);

  const pct = Math.min(100, (time / cap) * 100);

  return (
    <div className="card-dark rounded-3xl p-6 md:p-8 overflow-hidden relative">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 15% 20%, #d97524, transparent 55%)' }} />
      <div className="relative grid md:grid-cols-[minmax(0,300px)_1fr] gap-7 items-center">
        {/* Now playing */}
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            <img src={SOUNDTRACK.cover} alt={SOUNDTRACK.title} className="w-40 h-40 rounded-2xl object-cover shadow-xl" />
            <div className={`absolute inset-0 rounded-2xl flex items-center justify-center bg-fire-ink/40 transition-opacity ${playing ? 'opacity-0' : 'opacity-100'}`}>
              <Music2 className="w-9 h-9 text-fire-goldlight" />
            </div>
          </div>
          <span className="font-marcellus tracking-[0.25em] text-fire-gold text-[10px] mt-4">THE SOUND OF THE MOVEMENT</span>
          <h3 className="font-display text-2xl text-fire-cream mt-1 leading-tight">{track?.title}</h3>
          <p className="text-fire-cream/55 font-body text-xs">{SOUNDTRACK.title} · 30-sec preview</p>

          {/* Progress */}
          <div className="w-full mt-4">
            <div className="h-1.5 rounded-full bg-fire-ink/70 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-fire-ember to-fire-gold transition-all duration-200" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex justify-between mt-1.5 text-[11px] text-fire-cream/50 font-body">
              <span>{fmtTime(time)}</span>
              <span>0:{cap}</span>
            </div>
          </div>

          {/* Transport */}
          <div className="flex items-center gap-4 mt-4">
            <button onClick={prev} className="text-fire-cream/70 hover:text-fire-goldlight transition-colors" aria-label="Previous">
              <SkipBack className="w-5 h-5" />
            </button>
            <button onClick={toggle} className="btn-gold w-14 h-14 rounded-full flex items-center justify-center" aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
            </button>
            <button onClick={next} className="text-fire-cream/70 hover:text-fire-goldlight transition-colors" aria-label="Next">
              <SkipForward className="w-5 h-5" />
            </button>
          </div>

          <button onClick={addFull} className="btn-outline-gold rounded-full px-6 py-2.5 text-sm font-semibold inline-flex items-center gap-2 mt-5">
            <Disc3 className="w-4 h-4" /> Add Full Soundtrack · ${SOUNDTRACK.fullProduct.price}
          </button>
        </div>

        {/* Track list */}
        <div>
          <p className="font-marcellus tracking-widest text-fire-gold text-[11px] mb-3 uppercase">Tracklist · ${SOUNDTRACK.singlePrice} per single</p>
          <ul className="space-y-1.5">
            {tracks.map((t, i) => {
              const active = i === current;
              return (
                <li key={t.id} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${active ? 'bg-fire-gold/15 border border-fire-gold/30' : 'border border-transparent hover:bg-fire-gold/5'}`}>
                  <button
                    onClick={() => (active && playing ? toggle() : playIndex(i))}
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${active ? 'bg-fire-gold text-fire-ink' : 'border border-fire-gold/30 text-fire-cream/70 hover:text-fire-goldlight'}`}
                    aria-label={`Play ${t.title}`}
                  >
                    {active && playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
                  </button>
                  <button onClick={() => (active && playing ? toggle() : playIndex(i))} className="flex-1 text-left">
                    <span className={`font-cormorant text-lg ${active ? 'text-fire-goldlight' : 'text-fire-cream/85'}`}>{t.title}</span>
                  </button>
                  <span className="font-body text-[11px] text-fire-cream/40 mr-1 hidden sm:inline">0:30</span>
                  <button
                    onClick={() => addSingle(t)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-all ${added === t.id ? 'bg-fire-maroon text-fire-cream' : 'btn-gold'}`}
                  >
                    {added === t.id ? (<><Check className="w-3.5 h-3.5" /> Added</>) : (<><Plus className="w-3.5 h-3.5" /> ${t.price}</>)}
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="text-fire-cream/40 font-body text-[11px] mt-4">
            Previews are 30 seconds. Buy singles or the full soundtrack — checkout happens right here on the site.
          </p>
        </div>
      </div>
      <audio ref={audioRef} src={track?.src} preload="none" />
    </div>
  );
};

export default MusicPlayer;
